import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { Siren, Users, HeartPulse, MessagesSquare, PackageCheck } from 'lucide-react';

import Header from '../components/Dashboard/Header'
import Sidebar from '../layouts/Sidebar'
import PieCharts from '../components/Charts/PieChart'
import DashboardMap from '../components/Dashboard/DashboardMap';
import ConsumptionChart from '../components/Charts/ConsumptionChart';
import AnnouncementsList from '../components/Dashboard/AnnouncementsList';
import Card, { CardHeader } from '../components/ui/Card';
import KpiCard from '../components/ui/KpiCard';
import { PageLoader } from '../components/ui/Spinner';

import AnnouncementService from '../services/AnnouncementService';
import DashboardService from '../services/DashboardService';

const teamSeries = [
    { dataKey: 'active', label: "Active" },
    { dataKey: 'partiallyActive', label: "Partially Active" },
    { dataKey: 'inActive', label: "Inactive" },
]

const CBRNE_TYPES = [
    { key: 'Chemical', label: 'C', dot: 'bg-red-600' },
    { key: 'Biological', label: 'B', dot: 'bg-orange-600' },
    { key: 'Radiological', label: 'R', dot: 'bg-green-600' },
    { key: 'Nuclear', label: 'N', dot: 'bg-purple-600' },
    { key: 'Explosion', label: 'E', dot: 'bg-neutral-800' },
];

function Dashboard() {

    const [announcements, setAnnouncements] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadAnnouncements = async () => {
        try {
            let response = await AnnouncementService.findAll(1, 100);
            if (response?.status === 200) {
                setAnnouncements(response.data)
                if (response.data.length > announcements.length)
                    toast.info("New announcement:" + response.data[response.data.length - 1].title)
            }
        } catch (error) {
            console.log(error);
        }
    };

    const loadStats = async () => {
        try {
            const response = await DashboardService.getStats();
            if (response?.status === 200) {
                setStats(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        Promise.all([loadAnnouncements(), loadStats()]).finally(() => setLoading(false));

        const intervalId = setInterval(() => {
            loadAnnouncements();
            loadStats();
        }, 30000);

        return () => clearInterval(intervalId);
    }, []);

    const incidentsByType = stats?.incidentsByType || {};
    const drillsByType = stats?.drillsByType || {};
    const casualties = stats?.casualties || { low: 0, medium: 0, high: 0 };
    const teamDataset = stats?.teamDataset || [];
    const responses = stats?.responses || { accepted: 0, rejected: 0, pending: 0 };

    const patientData = [
        { value: casualties.low, label: 'Low' },
        { value: casualties.medium, label: 'Medium' },
        { value: casualties.high, label: 'High' },
    ].filter(d => d.value > 0);

    const totalCasualties = casualties.low + casualties.medium + casualties.high;
    const totalResponses = responses.accepted + responses.rejected + responses.pending;

    if (loading) {
        return (
            <Sidebar title="Admin Dashboard" header={<Header />}>
                <PageLoader label="Loading dashboard…" />
            </Sidebar>
        );
    }

    return (
        <Sidebar title="Admin Dashboard" header={<Header />}>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    <KpiCard
                        label="Active Incidents"
                        value={stats?.totalIncidents ?? 0}
                        note={`${stats?.totalDrills ?? 0} drills logged`}
                        icon={Siren}
                        type="red"
                    />
                    <KpiCard
                        label="Teams Mobilized"
                        value={stats?.totalTeams ?? 0}
                        note={`${stats?.totalMembers ?? 0} members enrolled`}
                        icon={Users}
                        type="green"
                    />
                    <KpiCard
                        label="Casualties Logged"
                        value={totalCasualties}
                        note={`${casualties.high} high • ${casualties.medium} medium • ${casualties.low} low`}
                        icon={HeartPulse}
                        type="blue"
                    />
                    <KpiCard
                        label="Member Response"
                        value={`${responses.accepted}/${totalResponses}`}
                        note={`${responses.pending} pending • ${responses.rejected} rejected`}
                        icon={MessagesSquare}
                        type="amber"
                    />
                    <KpiCard
                        label="Total Announcements"
                        value={stats?.totalAnnouncements ?? 0}
                        note="Incidents & drills combined"
                        icon={PackageCheck}
                    />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2">
                        <DashboardMap announcements={announcements} height="520px" />
                    </div>

                    <Card padded={false} className="flex flex-col overflow-hidden" style={{ minHeight: 520 }}>
                        <CardHeader title="Incident Navigation" subtitle={`${announcements.length} total`} />
                        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 464 }}>
                            <AnnouncementsList announcements={announcements} />
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <Card>
                        <h3 className="font-bold text-neutral-800 text-sm mb-4">CBRNE Breakdown</h3>
                        <div className="space-y-5">
                            <div>
                                <p className="kpi-label mb-3">Incidents ({stats?.totalIncidents ?? 0})</p>
                                <div className="flex items-center justify-between">
                                    {CBRNE_TYPES.map((t) => (
                                        <div key={t.key} className="flex flex-col items-center gap-1.5">
                                            <span className={`w-7 h-7 rounded-full ${t.dot} text-white text-xs font-bold flex items-center justify-center`}>
                                                {t.label}
                                            </span>
                                            <span className="text-sm font-bold text-neutral-800">{incidentsByType[t.key] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="pt-4 border-t border-neutral-100">
                                <p className="kpi-label mb-3">Drills ({stats?.totalDrills ?? 0})</p>
                                <div className="flex items-center justify-between">
                                    {CBRNE_TYPES.map((t) => (
                                        <div key={t.key} className="flex flex-col items-center gap-1.5">
                                            <span className={`w-7 h-7 rounded-full ${t.dot} text-white text-xs font-bold flex items-center justify-center`}>
                                                {t.label}
                                            </span>
                                            <span className="text-sm font-bold text-neutral-800">{drillsByType[t.key] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <PieCharts
                            label="Casualties"
                            data={patientData.length > 0 ? patientData : [{ value: 1, label: 'None logged' }]}
                            height={200}
                        />
                        <p className="kpi-note mt-2">
                            {casualties.high} high • {casualties.medium} medium • {casualties.low} low
                        </p>
                    </Card>

                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-neutral-800 text-sm">Team Response</h3>
                            <span className="text-xs text-neutral-400 font-medium">
                                {stats?.totalMembers ?? 0} members • {stats?.totalTeams ?? 0} teams
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-4 text-xs font-semibold">
                            <span className="flex items-center gap-1.5 text-green-700">
                                <span className="w-2 h-2 rounded-full bg-green-600" />
                                Accepted {responses.accepted}
                            </span>
                            <span className="flex items-center gap-1.5 text-red-700">
                                <span className="w-2 h-2 rounded-full bg-red-600" />
                                Rejected {responses.rejected}
                            </span>
                            <span className="flex items-center gap-1.5 text-orange-700">
                                <span className="w-2 h-2 rounded-full bg-orange-600" />
                                Pending {responses.pending}
                            </span>
                        </div>
                        {teamDataset.length > 0 ? (
                            <div className="overflow-x-auto">
                                <ConsumptionChart
                                    data={teamDataset}
                                    series={teamSeries}
                                    height={200}
                                    width={Math.max(320, teamDataset.length * 90)}
                                />
                            </div>
                        ) : (
                            <div className="text-sm text-neutral-400 text-center py-10">No team data yet</div>
                        )}
                    </Card>
                </div>
            </div>
        </Sidebar>
    )
}

export default Dashboard
